import React, { useContext } from 'react'
import lodash from 'lodash'
import glamorous from 'glamorous'
import PropTypes from 'prop-types'
import { graphql, withApollo } from 'react-apollo'
import { CurrencyFormat } from '@paypalcorp/worldready'
import {
  Message,
  Context as WorldReadyContext,
} from '@paypalcorp/worldready-react'

import glamorousIcon from '../shared/icons'
import { AlertSvg } from '../shared/icons/svg-images'
import { ProductDiv } from '../shared/glamorous-style'
import { trackImpression } from '../../utils/analytics'
import getStoreErrorMsg from '../../graphql/getStoreErrorMsg'

const Content = glamorous.div({
  backgroundColor: '#ffffff',
  textAlign: 'center',
  '@media only screen and (maxWidth: 768px)': {
    margin: '0 12px',
  },
})

const Header = glamorous.div(
  {
    fontSize: '20px',
    color: '#2c2e2f',
    paddingBottom: '24px',
  },
  'vx_text-4_regular',
)

const Text = glamorous.div(
  {
    fontSize: '18px',
    color: '#2b2b2b',
    textAlign: 'left',
    margin: '0 46px',
    paddingBottom: '36px',
    '@media only screen and (maxWidth: 768px)': {
      margin: '0 12px',
    },
  },
  'vx_text-body-lg',
)

const PromotionErrorText = glamorous.div(
  {
    fontSize: '20px',
    color: '#2c2e2f',
    textAlign: 'center',
    margin: '0 150px',
    paddingBottom: '36px',
    '@media only screen and (maxWidth: 768px)': {
      margin: '0 12px',
    },
  },
  'vx_text-body-lg',
)

const TxnErrorText = PromotionErrorText
const VelocityLimitText = PromotionErrorText

const Button = glamorous.button({
  marginBottom: '60px',
  width: '270px',
  '@media only screen and (maxWidth: 768px)': {
    width: '271px',
  },
})

const IconContainer = glamorous.div({
  paddingTop: '103px',
  paddingBottom: '71px',
})

const AlertICON = glamorousIcon({
  '& svg': { height: '102', width: '102' },
})

AlertICON.defaultProps = { svg: AlertSvg }

const Error = ({
  history,
  data,
  title,
  routeTo,
  buttonText,
  promotionError = [],
  txnLimitErrors = [],
  velocityLimitErrors = [],
}) => {
  const worldready = useContext(WorldReadyContext)

  trackImpression(
    'main:consumer:gifts:home:errorpage',
    'main:consumer:gifts:home:errorpage:::',
  )

  if (data.loading) {
    return (
      <div className="vx_has-spinner-large" style={{ paddingTop: '400px' }} />
    )
  }
  const storeErrorMsg = lodash.get(data, 'getStoreInfo.store_error_msg')

  return (
    <div id="error-page">
      <ProductDiv>
        <Content id="content">
          <IconContainer id="icon">
            <AlertICON />
          </IconContainer>
          {title && <Header>{title}</Header>}
          {txnLimitErrors.length > 0 && (
            <div>
              {txnLimitErrors.map((err, i) => (
                <TxnErrorText key={`txn-limit-err-${i}`}>
                  <Message
                    id="pages/error.error.transactionLimitExceed.header"
                    amount={new CurrencyFormat(worldready, {
                      currency: err.currency || 'USD',
                      maximumFractionDigits: 0,
                    }).format(err.limit)}
                    productName={err.productName}
                  />
                </TxnErrorText>
              ))}
            </div>
          )}
          {velocityLimitErrors.length > 0 && (
            <div>
              {velocityLimitErrors.map((err, i) => (
                <VelocityLimitText key={`velocity-limit-err-${i}`}>
                  <Message
                    id="pages/error.error.perDayLimitExceed.header"
                    amount={new CurrencyFormat(worldready, {
                      currency: err.currency || 'USD',
                      maximumFractionDigits: 0,
                    }).format(err.limit)}
                    productName={err.productName}
                  />
                </VelocityLimitText>
              ))}
            </div>
          )}
          {promotionError.length > 0 && (
            <div>
              <PromotionErrorText>{promotionError[0]}</PromotionErrorText>
              <PromotionErrorText>{promotionError[1]}</PromotionErrorText>
            </div>
          )}
          {txnLimitErrors.length !== 0 &&
            promotionError.length !== 0 &&
            velocityLimitErrors.length !==
              0(
                <Text>
                  {storeErrorMsg ? (
                    <div>{storeErrorMsg}</div>
                  ) : (
                    <div>
                      <Message id="pages/error.error.text" />
                    </div>
                  )}
                  <div>
                    <Message id="pages/error.error.charges" />
                  </div>
                </Text>,
              )}
          <Button
            className="vx_btn vx_btn-secondary"
            onClick={() => history.push(routeTo)}
          >
            {buttonText}
          </Button>
        </Content>
      </ProductDiv>
    </div>
  )
}

Error.propTypes = {
  history: PropTypes.object.isRequired,
  data: PropTypes.shape({
    loading: PropTypes.bool,
    error: PropTypes.object,
    getStoreErrorMsg: PropTypes.shape({
      store_error_msg: PropTypes.string,
    }),
  }),
  routeTo: PropTypes.string.isRequired,
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  buttonText: PropTypes.oneOfType([PropTypes.string, PropTypes.element])
    .isRequired,
  promotionError: PropTypes.array,
  txnLimitErrors: PropTypes.array,
  velocityLimitErrors: PropTypes.array,
}

const ErrorWithGraphql = lodash.flowRight(
  withApollo,
  graphql(getStoreErrorMsg),
)(Error)

export default ErrorWithGraphql
